import { useEffect, useMemo, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { ActiveCashier } from '@core/components/PinPadModal'
import { db } from '@core/db/db'
import { refreshCloudAdminSession, restoreCloudAdminSession } from '@core/auth/cloudSession'
import {
  applyRoleNavigation,
  DEFAULT_ROLE_NAVIGATION,
  defaultSectionForRole,
  resolveRolePreset,
  type RoleNavigationConfig
} from '@core/auth/rolePresets'
import { loadRoleNavigation, pushLocalRoleNavigation } from '@core/auth/roleNavigation'
import { AdminNav } from './admin/AdminNav'
import { ADMIN_SECTIONS, isSectionAllowed, type AdminSectionEntry } from './admin/sections'
import type { AdminPermissions, AdminSectionId } from './admin/types'

/** Ключ meta: последний открытый раздел админки. */
const META_ADMIN_SECTION = 'adminSection'

interface AdminPageProps {
  core: AppCore
  /** Активный кассир — для RBAC на действия и видимость разделов. */
  cashier?: ActiveCashier | null
  /** Компактный лэйаут: навигация выпадающим списком вместо колонки. */
  compact?: boolean
  /** Открыть привязку мобильной кассы (проброшено из App). */
  onPairDevice?: () => void
  /** Устройство уже сопряжено — кнопку привязки не показываем. */
  devicePaired?: boolean
  /** Первичная настройка: только облако, без остальных разделов. */
  setupOnly?: boolean
}

/**
 * Админка директора — оболочка с навигацией по разделам.
 *
 * Рендерится только активный раздел (реестр `admin/sections.ts`), выбор
 * запоминается в db.meta и восстанавливается при открытии. Если сохранённый
 * раздел недоступен по правам — откат к «Обзор».
 */
export function AdminPage({
  core,
  cashier,
  compact = false,
  onPairDevice,
  devicePaired = false,
  setupOnly = false
}: AdminPageProps) {
  const perms: AdminPermissions = {
    canManage: cashier?.permissions?.can_manage_shifts !== false,
    canManageCloud: setupOnly || cashier?.role === 'admin',
    canCorrect:
      cashier?.permissions?.can_correction ?? cashier?.permissions?.can_manage_shifts !== false,
    // Без PIN-входа действует облачная учётная запись директора (её токен уже
    // даёт доступ к админке), поэтому одобрение заявок разрешено. Когда кассир
    // авторизован по PIN — решают его права (сервер проверит reviewed_by_user_id).
    canEditCatalog:
      cashier == null || cashier.permissions?.can_edit_catalog === true || cashier.role === 'admin',
    canApproveMarkingHold:
      cashier == null ||
      cashier.role === 'admin' ||
      cashier.permissions?.can_approve_marking_hold === true,
    canWriteoff:
      cashier == null ||
      cashier.role === 'admin' ||
      cashier.permissions?.can_writeoff === true
  }

  const rolePreset = resolveRolePreset(cashier)
  const [roleConfig, setRoleConfig] = useState<RoleNavigationConfig>(DEFAULT_ROLE_NAVIGATION)

  // Раскладка по ролям: облако — источник истины, локальный кэш — для офлайна.
  useEffect(() => {
    if (setupOnly) return
    let cancelled = false
    void loadRoleNavigation().then((config) => {
      if (!cancelled) setRoleConfig(config)
    })
    // Локальная правка, сделанная офлайн, уходит в облако при открытии админки.
    if (perms.canManageCloud) void pushLocalRoleNavigation().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [setupOnly, perms.canManageCloud])

  const visibleSections = useMemo(() => {
    if (setupOnly) return ADMIN_SECTIONS.filter((s) => s.id === 'integrations')
    const allowed = ADMIN_SECTIONS.filter((s) => isSectionAllowed(s, perms))
    return applyRoleNavigation(allowed, rolePreset, roleConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupOnly, perms.canManage, perms.canManageCloud, perms.canCorrect, rolePreset, roleConfig])

  const [active, setActive] = useState<AdminSectionId>(
    setupOnly ? 'integrations' : 'overview'
  )

  /** Первый доступный раздел — безопасный fallback вместо глобального Overview. */
  const firstVisible = visibleSections[0]?.id ?? 'overview'
  const preferredSection = defaultSectionForRole(rolePreset)
  const fallbackSection = visibleSections.some((s) => s.id === preferredSection)
    ? preferredSection
    : firstVisible

  // Access-token живёт ограниченно: обновляем его в фоне и при возврате на вкладку.
  // Только для тех, кому вообще разрешены облачные разделы.
  useEffect(() => {
    if (!perms.canManageCloud) return
    void restoreCloudAdminSession()
    const timer = window.setInterval(() => void refreshCloudAdminSession(), 10 * 60_000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshCloudAdminSession()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [perms.canManageCloud])

  // Восстановление последнего раздела (один раз при открытии админки).
  useEffect(() => {
    void db.meta
      .get(META_ADMIN_SECTION)
      .then((entry) => {
        const saved = typeof entry?.value === 'string' ? (entry.value as AdminSectionId) : null
        const allowed =
          saved && visibleSections.some((s) => s.id === saved) ? saved : fallbackSection
        setActive(allowed)
      })
      .catch(() => setActive(fallbackSection))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupOnly])

  // Права или раскладка могли измениться — недоступный раздел закрываем.
  useEffect(() => {
    if (!visibleSections.some((s) => s.id === active)) setActive(fallbackSection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, setupOnly, perms.canManage, perms.canCorrect, roleConfig, rolePreset])

  const selectSection = (id: AdminSectionId) => {
    setActive(id)
    void db.meta.put({ key: META_ADMIN_SECTION, value: id }).catch(() => undefined)
  }

  const entry: AdminSectionEntry =
    ADMIN_SECTIONS.find((s) => s.id === active) ??
    visibleSections[0] ??
    ADMIN_SECTIONS[0]
  const Section = entry.Component

  return (
    <section>
      <div className={compact ? 'block min-w-0' : 'flex items-start gap-4'}>
        <AdminNav
          items={visibleSections.map((s) => ({ id: s.id, label: s.label, group: s.group }))}
          active={active}
          onSelect={selectSection}
          compact={compact}
        />
        <div className="min-w-0 flex-1">
          <h2 className="mt-0 text-huge font-bold text-ink">{entry.label}</h2>
          <Section
            core={core}
            cashier={cashier}
            perms={perms}
            onPairDevice={onPairDevice}
            devicePaired={devicePaired}
            setupOnly={setupOnly}
          />
        </div>
      </div>
    </section>
  )
}
